import { __ } from "@wordpress/i18n";
import { useSettings } from "../../context/SettingsContext";
import { Toggle, ButtonGroup, SettingRow, SectionCard } from "../ui/index";

export default function VideoPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title={__("How to Add Videos", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Videos are added per-product. Go to the product edit page in WooCommerce and use the 'Product Gallery Videos' metabox in the sidebar to add YouTube, Vimeo, or uploaded video files.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <div className="mt-2 mb-2 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="mt-0.5 size-3.5 shrink-0 text-blue-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>
                        {__("Videos appear after product images in the gallery thumbnail strip. Clicking a video thumbnail plays it in the main display area. These settings control playback behaviour across all products using this rule.", "wpbean-product-gallery-slider-for-woocommerce")}
                    </span>
                </div>
            </SectionCard>

            <SectionCard
                title={__("General", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Global video behaviour settings.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Video Position", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Where product videos appear in the gallery thumbnail strip — before or after product images.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <ButtonGroup
                        value={settings.videoPosition}
                        onChange={(v) => s("videoPosition", v)}
                        options={[
                            { label: __("Start", "wpbean-product-gallery-slider-for-woocommerce"), value: "start" },
                            { label: __("End", "wpbean-product-gallery-slider-for-woocommerce"),   value: "end"   },
                        ]}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Embed Playback", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Settings for YouTube and Vimeo embeds.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Autoplay", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Automatically start playing the embed when navigated to. Most browsers require the video to also be muted for autoplay to work.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoAutoplay}
                        onChange={(v) => s("videoAutoplay", v)}
                    />
                </SettingRow>

                {settings.videoAutoplay && (
                    <SettingRow
                        label={__("Muted", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Mute the embed by default. Required for autoplay in most browsers. Users can unmute inside the player.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <Toggle
                            value={settings.videoMuted}
                            onChange={(v) => s("videoMuted", v)}
                        />
                    </SettingRow>
                )}

                <SettingRow
                    label={__("Loop", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Restart the video from the beginning when it ends.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoLoop}
                        onChange={(v) => s("videoLoop", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Hide Related Videos", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("For YouTube: hide suggested videos shown after the video ends (sets rel=0).", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoHideRelated}
                        onChange={(v) => s("videoHideRelated", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Show Controls", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Display the player controls bar inside the YouTube / Vimeo embed (play, pause, volume, fullscreen).", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoEmbedControls}
                        onChange={(v) => s("videoEmbedControls", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Fit to Container", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Stretch the embed to fill the full width and height of the main image area. The video will be cropped as needed to remove any empty space.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoEmbedFitContainer}
                        onChange={(v) => s("videoEmbedFitContainer", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Hosted Video", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Settings for video files uploaded to the WordPress media library.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Autoplay", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Automatically start playing the hosted video when navigated to. For silent autoplay, enable Muted as well.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoHostedAutoplay}
                        onChange={(v) => s("videoHostedAutoplay", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Muted", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Mute hosted videos by default. Required for autoplay in most browsers. Useful for background-style videos.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoHostedMuted}
                        onChange={(v) => s("videoHostedMuted", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Show Controls", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Display the browser's native video controls (play, pause, seek, volume).", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoControls}
                        onChange={(v) => s("videoControls", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Loop", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Restart hosted videos from the beginning when they end.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoHostedLoop}
                        onChange={(v) => s("videoHostedLoop", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Fit to Container", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Stretch the video to fill the full width and height of the main image area. The video will be cropped as needed to remove any empty space.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.videoHostedFitContainer}
                        onChange={(v) => s("videoHostedFitContainer", v)}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}
